import React from 'react';
import { observer } from 'mobx-react-lite';
import {
   Badge,
   Box,
   Button,
   Checkbox,
   Flex,
   Heading,
   ScrollArea,
   Table,
   Text,
   TextField,
} from '@radix-ui/themes';
import { useSearchParams } from 'react-router-dom';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import { AdminSideBar } from 'admin/elements/side-bar';
import { Paginator } from 'shared/components/pagination';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { useResumeManager } from './resume-manager.provider';
import { ResumeManagerFilters } from './resume-manager-filters';
import { ResumeActionsMenu } from './resume-actions-menu';

import './resume-manager.scss';

const SKELETON_ROWS = 12;

const renderUpdatedDate = (date?: string) => {
   if (!date) {
      return '-';
   }

   return DateUtilsService.toAppFormat(date);
};

const parsePageParam = (pageParam: string | null): number => {
   const parsedPage = Number(pageParam);

   if (!Number.isFinite(parsedPage) || parsedPage < 1) {
      return 1;
   }

   return Math.floor(parsedPage);
};

function ResumeManagerView() {
   const resumeManager = useResumeManager();
   const [searchParams, setSearchParams] = useSearchParams();
   const pageParam = searchParams.get('page');

   const setPageQuery = React.useCallback(
      (page: number) => {
         const nextPage = Math.max(page, 0) + 1;
         if (parsePageParam(pageParam) === nextPage) {
            return;
         }

         const nextSearchParams = new URLSearchParams(searchParams);
         nextSearchParams.set('page', String(nextPage));
         setSearchParams(nextSearchParams, { replace: true });
      },
      [pageParam, searchParams, setSearchParams]
   );

   React.useEffect(() => {
      const queryPage = parsePageParam(pageParam);
      const normalizedPageParam = String(queryPage);

      if (pageParam !== normalizedPageParam) {
         const nextSearchParams = new URLSearchParams(searchParams);
         nextSearchParams.set('page', normalizedPageParam);
         setSearchParams(nextSearchParams, { replace: true });
         return;
      }

      const requestedPage = queryPage - 1;
      if (
         resumeManager.loadResumesState.status !== 'idle' &&
         resumeManager.pagination.page === requestedPage
      ) {
         return;
      }

      resumeManager.loadPage(requestedPage);
   }, [pageParam, searchParams, setSearchParams, resumeManager]);

   const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, index) => index);

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="resume-manager">
            <Flex width="270px" minWidth="270px">
               <AdminSideBar />
            </Flex>
            <Box className="resume-manager__content">
               <Flex py="1" mx="3" justify="between" align="center" gap="3">
                  <Heading as="h1" weight="medium">
                     Resume List
                  </Heading>

                  <Flex align="center" gap="2">
                     <Text size="3" color="gray">
                        Records:
                     </Text>
                     <Text size="3" weight="medium">
                        {resumeManager.recordStart} - {resumeManager.recordEnd} of{' '}
                        {resumeManager.totalCount}
                     </Text>
                  </Flex>
               </Flex>

               <ResumeManagerFilters />

               <ScrollArea scrollbars="both" type="always" className="resume-manager__scroll-area">
                  <Box p="3" className="resume-manager__table-wrap">
                     <Table.Root size="3" layout="auto" variant="surface">
                        <Table.Header>
                           <Table.Row>
                              <Table.ColumnHeaderCell width="80px" maxWidth="80px" align="center">
                                 <Checkbox
                                    color="blue"
                                    size="2"
                                    highContrast
                                    checked={resumeManager.selectedAll}
                                    disabled={resumeManager.loading || resumeManager.resumes.length === 0}
                                    onCheckedChange={(checked) => {
                                       if (checked) {
                                          resumeManager.onSelectAll();
                                       } else {
                                          resumeManager.onDeselectAll();
                                       }
                                    }}
                                 />
                              </Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Jobseeker ID</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Jobseeker Name</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Resume ID</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Resume Name</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Location</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Updated</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell justify="center">Action</Table.ColumnHeaderCell>
                           </Table.Row>
                        </Table.Header>

                        <Table.Body>
                           {resumeManager.loading &&
                              skeletonRows.map((row) => (
                                 <Table.Row key={`skeleton-${row}`} className="table-skeleton-row">
                                    <Table.Cell width="80px" justify="center">
                                       <div className="table-skeleton table-skeleton--checkbox" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--id" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--name" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--email" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--id" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <Flex direction="column" gap="2">
                                          <div className="table-skeleton table-skeleton--title" />
                                          <div className="table-skeleton table-skeleton--badge" />
                                       </Flex>
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--location" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--category" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--date" />
                                    </Table.Cell>
                                    <Table.Cell justify="center" className="action-cell">
                                       <Flex justify="center" gap="2">
                                          <div className="table-skeleton table-skeleton--button" />
                                          <div className="table-skeleton table-skeleton--button" />
                                       </Flex>
                                    </Table.Cell>
                                 </Table.Row>
                              ))}

                           {!resumeManager.loading && resumeManager.resumes.length === 0 && (
                              <Table.Row>
                                 <Table.Cell colSpan={10} align="center">
                                    No records found
                                 </Table.Cell>
                              </Table.Row>
                           )}

                           {!resumeManager.loading &&
                              resumeManager.resumes.map((resume) => {
                                 const categoryLabel = Array.isArray(resume.category)
                                    ? resume.category.join(', ')
                                    : (resume.category_name ?? '-');
                                 const updatedAt = resume.updated ?? resume.modified;

                                 return (
                                    <Table.Row key={resume.id}>
                                       <Table.Cell width="80px" justify="center">
                                          <Checkbox
                                             color="blue"
                                             size="2"
                                             highContrast
                                             checked={resumeManager.idsArray.includes(resume.id)}
                                             onCheckedChange={() => {
                                                resumeManager.onToggleById(resume.id);
                                             }}
                                          />
                                       </Table.Cell>
                                       <Table.Cell>{resume.jobseeker_id}</Table.Cell>
                                       <Table.Cell>{resume.jobseeker_name}</Table.Cell>
                                       <Table.Cell>{resume.jobseeker_email}</Table.Cell>
                                       <Table.Cell>{resume.id}</Table.Cell>
                                       <Table.Cell>
                                          <Flex direction="column" gap="1">
                                             <Text>{resume.position}</Text>
                                             {resume.resume_verified === 1 && (
                                                <Badge color="green" variant="soft" radius="full">
                                                   Verified
                                                </Badge>
                                             )}
                                          </Flex>
                                       </Table.Cell>
                                       <Table.Cell>{resume.city?.city_name ?? '-'}</Table.Cell>
                                       <Table.Cell>{categoryLabel}</Table.Cell>
                                       <Table.Cell>{renderUpdatedDate(updatedAt)}</Table.Cell>
                                       <Table.Cell justify="center" className="action-cell">
                                          <ResumeActionsMenu resume={resume} />
                                       </Table.Cell>
                                    </Table.Row>
                                 );
                              })}
                        </Table.Body>
                     </Table.Root>
                  </Box>
               </ScrollArea>

               <Flex className="resume-manager__pagination" py="2" px="3">
                  <Flex gap="4" px="3">
                     <Button variant="surface" size="3" color="blue" disabled>
                        <Text weight="regular" size="2">
                           ACTIVATE
                        </Text>
                     </Button>

                     <Button variant="surface" size="3" color="blue" disabled>
                        <Text weight="regular" size="2">
                           DEACTIVATE
                        </Text>
                     </Button>

                     <Button size="3" color="red" disabled>
                        <Text weight="regular" size="2">
                           DELETE
                        </Text>
                     </Button>
                  </Flex>

                  <Paginator
                     totalPages={resumeManager.totalPages}
                     currentPage={resumeManager.pagination.page}
                     disabled={resumeManager.loading}
                     onPageClick={(page) => {
                        setPageQuery(page);
                     }}
                     setRange={(range) => {
                        resumeManager.pagination.setRange(range);
                     }}
                     range={resumeManager.pagination.range}
                  />

                  <Flex align="center" gap="2">
                     <Flex align="center" gap="1">
                        <Text size="3" color="gray">
                           Total pages:
                        </Text>
                        <Text size="3" weight="medium">
                           {resumeManager.totalPages === 0 ? '' : resumeManager.totalPages}
                        </Text>
                     </Flex>

                     <TextField.Root
                        className="resume-manager__page-input"
                        value={resumeManager.toPage}
                        disabled={resumeManager.loading}
                        onChange={(e) => {
                           resumeManager.onToPageChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              e.preventDefault();

                              const rawPage = resumeManager.toPage === '' ? 1 : Number(resumeManager.toPage);
                              if (!Number.isFinite(rawPage)) {
                                 return;
                              }

                              const maxPage = resumeManager.totalPages || 1;
                              const nextPage = Math.min(Math.max(rawPage, 1), maxPage) - 1;
                              setPageQuery(nextPage);
                           }
                        }}
                        size="3"
                        color="blue"
                        type="number"
                        min={1}
                        max={resumeManager.totalPages}
                     />
                  </Flex>
               </Flex>
            </Box>
         </Box>
      </>
   );
}

export const ResumeManager = observer(ResumeManagerView);
